import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Bot, CheckCircle2, XCircle, Clock, FileText, Mail, Loader2,
  Filter, Pencil, Save, X, Linkedin, Facebook, Instagram,
  AlertTriangle, Eye, Sparkles, BarChart3, Send
} from "lucide-react";

const PLATFORM_CONFIG = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877f2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#e1306c" },
};

const STATUS_CONFIG = {
  brouillon: { label: "Brouillon", bg: "bg-slate-500/10 text-slate-400", icon: FileText },
  en_attente_validation: { label: "En attente", bg: "bg-amber-500/10 text-amber-400", icon: Clock },
  valide: { label: "Valide", bg: "bg-blue-500/10 text-blue-400", icon: CheckCircle2 },
  planifie: { label: "Planifie", bg: "bg-indigo-500/10 text-indigo-400", icon: Clock },
  publie: { label: "Publie", bg: "bg-green-500/10 text-green-400", icon: CheckCircle2 },
  rejete: { label: "Rejete", bg: "bg-red-500/10 text-red-400", icon: XCircle },
  envoyee: { label: "Envoyee", bg: "bg-green-500/10 text-green-400", icon: Send },
};

export function AgentTasksDashboard({ api }) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, published: 0 });
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Edit dialog
  const [showEdit, setShowEdit] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({ content: "", hashtags: [], platforms: [], scheduled_at: "", subject: "" });
  const [saving, setSaving] = useState(false);

  // Reject dialog
  const [showReject, setShowReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Preview
  const [showPreview, setShowPreview] = useState(null);

  useEffect(() => { loadTasks(); }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/agent-tasks");
      setTasks(r.data.tasks || []);
      setStats(r.data.stats || {});
    } catch { setTasks([]); }
    setLoading(false);
  };

  const filtered = tasks.filter(t => {
    if (filterType && t.type !== filterType) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    return true;
  });

  const validateTask = async (taskId) => {
    setSaving(true);
    try {
      await api.put(`/marketing/agent-tasks/${taskId}`, { action: "validate" });
      loadTasks();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const rejectTask = async (taskId) => {
    setSaving(true);
    try {
      await api.put(`/marketing/agent-tasks/${taskId}`, { action: "reject", reason: rejectReason });
      setShowReject(null);
      setRejectReason("");
      loadTasks();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const openEdit = (task) => {
    setEditTask(task);
    setEditForm({
      content: task.content || "",
      hashtags: task.hashtags || [],
      platforms: task.platforms || [],
      scheduled_at: task.scheduled_at || "",
      subject: task.subject || "",
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/marketing/agent-tasks/${editTask.task_id}`, { action: "update", ...editForm });
      setShowEdit(false);
      setEditTask(null);
      loadTasks();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#10b981]" /></div>;

  return (
    <div data-testid="agent-tasks-dashboard" className="space-y-5">
      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
          <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><Bot className="w-3 h-3" /> Total</div>
          <div className="text-xl font-black text-white">{stats.total}</div>
        </div>
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
          <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><Clock className="w-3 h-3 text-amber-400" /> En attente</div>
          <div className="text-xl font-black text-amber-400">{stats.pending}</div>
        </div>
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
          <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-blue-400" /> Approuves</div>
          <div className="text-xl font-black text-blue-400">{stats.approved}</div>
        </div>
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
          <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><XCircle className="w-3 h-3 text-red-400" /> Rejetes</div>
          <div className="text-xl font-black text-red-400">{stats.rejected}</div>
        </div>
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
          <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-400" /> Publies</div>
          <div className="text-xl font-black text-green-400">{stats.published}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-1 text-[10px] text-slate-500"><Filter className="w-3 h-3" /> Filtres :</div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1 text-[11px]" data-testid="filter-task-type">
          <option value="">Tous types</option>
          <option value="post">Posts</option>
          <option value="campaign">Campagnes</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1 text-[11px]" data-testid="filter-task-status">
          <option value="">Tous statuts</option>
          <option value="en_attente_validation">En attente</option>
          <option value="brouillon">Brouillon</option>
          <option value="valide">Valide</option>
          <option value="planifie">Planifie</option>
          <option value="rejete">Rejete</option>
          <option value="publie">Publie</option>
        </select>
        <span className="text-[10px] text-slate-500 self-center ml-auto">{filtered.length} tache{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Tasks list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-[#14101c] border border-white/5 rounded-xl">
          <Bot className="w-14 h-14 mx-auto mb-4 text-slate-600" />
          <p>Aucune tache de l'Agent IA</p>
          <p className="text-xs mt-1">Les posts et campagnes generes par l'agent apparaitront ici</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(task => {
            const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.brouillon;
            const StatusIcon = statusCfg.icon;
            const isPost = task.type === "post";
            const isPending = task.status === "en_attente_validation" || task.status === "brouillon";

            return (
              <div key={task.task_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`task-${task.task_id}`}>
                <div className="flex items-start gap-3">
                  {/* Type + platform icons */}
                  <div className="flex flex-col items-center gap-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPost ? "bg-[#10b981]/10" : "bg-[#3b82f6]/10"}`}>
                      {isPost ? <FileText className="w-4 h-4 text-[#10b981]" /> : <Mail className="w-4 h-4 text-[#3b82f6]" />}
                    </div>
                    {isPost && (task.platforms || []).map(pl => {
                      const cfg = PLATFORM_CONFIG[pl];
                      if (!cfg) return null;
                      const Icon = cfg.icon;
                      return <Icon key={pl} className="w-3 h-3" style={{ color: cfg.color }} />;
                    })}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${isPost ? "bg-[#10b981]/10 text-[#10b981]" : "bg-[#3b82f6]/10 text-[#3b82f6]"}`}>
                        {isPost ? "Post" : "Campagne"}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${statusCfg.bg}`}>
                        <StatusIcon className="w-2.5 h-2.5" /> {statusCfg.label}
                      </span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#10b981]/10 text-[#10b981] flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> IA
                      </span>
                      {task.pipeline_batch && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#3b82f6]/10 text-[#3b82f6]">Pipeline</span>
                      )}
                      {task.scheduled_at && (
                        <span className="text-[10px] text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(task.scheduled_at).toLocaleDateString("fr-FR")} {task.scheduled_at.includes("T") ? task.scheduled_at.split("T")[1]?.substring(0, 5) : ""}
                        </span>
                      )}
                      <span className="text-[9px] text-slate-600">{new Date(task.created_at).toLocaleDateString("fr-FR")}</span>
                    </div>

                    {/* Name for campaigns */}
                    {!isPost && task.name && <p className="text-xs font-bold text-white mb-1">{task.name}</p>}
                    {!isPost && task.subject && <p className="text-[10px] text-slate-400 mb-1">Objet : {task.subject}</p>}

                    {/* Content preview */}
                    <p className="text-xs text-slate-300 whitespace-pre-wrap line-clamp-3">{task.content}</p>

                    {/* Hashtags */}
                    {isPost && task.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {task.hashtags.map((h, i) => (
                          <span key={i} className="text-[9px] text-[#10b981]">#{h}</span>
                        ))}
                      </div>
                    )}

                    {/* Prompt info */}
                    {task.prompt_used && (
                      <div className="mt-1.5 text-[9px] text-slate-600 flex items-center gap-1">
                        <Bot className="w-3 h-3" /> Prompt: {task.prompt_used.substring(0, 80)}...
                      </div>
                    )}

                    {/* Rejection info */}
                    {task.status === "rejete" && task.validation?.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-500/5 border border-red-500/10 rounded-lg">
                        <span className="text-[10px] text-red-400 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Motif : {task.validation.rejection_reason}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    {isPending && (
                      <>
                        <button onClick={() => validateTask(task.task_id)}
                          className="p-1.5 hover:bg-green-500/10 rounded transition-colors" title="Valider"
                          data-testid={`validate-task-${task.task_id}`}>
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        </button>
                        <button onClick={() => { setShowReject(task.task_id); setRejectReason(""); }}
                          className="p-1.5 hover:bg-red-500/10 rounded transition-colors" title="Rejeter"
                          data-testid={`reject-task-${task.task_id}`}>
                          <XCircle className="w-4 h-4 text-red-400" />
                        </button>
                      </>
                    )}
                    <button onClick={() => openEdit(task)}
                      className="p-1.5 hover:bg-white/5 rounded transition-colors" title="Modifier">
                      <Pencil className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    <button onClick={() => setShowPreview(task)}
                      className="p-1.5 hover:bg-white/5 rounded transition-colors" title="Apercu">
                      <Eye className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!showReject} onOpenChange={(v) => { if (!v) setShowReject(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Rejeter la tache</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="Motif du rejet ou instructions de correction..."
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm h-24 resize-none"
              data-testid="task-reject-reason" />
            <div className="flex gap-2">
              <Button onClick={() => rejectTask(showReject)} className="flex-1 bg-red-500 text-white hover:bg-red-600" disabled={saving}
                data-testid="confirm-task-reject">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Rejeter
              </Button>
              <Button onClick={() => setShowReject(null)} variant="outline" className="border-white/10 text-slate-400">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={(v) => { setShowEdit(v); if (!v) setEditTask(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Corriger avant approbation</DialogTitle></DialogHeader>
          {editTask && (
            <div className="space-y-3 mt-2">
              {editTask.type === "campaign" && (
                <Input value={editForm.subject} onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="Objet" className="bg-[#0b0811] border-white/10 text-white" />
              )}
              <textarea value={editForm.content} onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))}
                className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm h-40 resize-none"
                data-testid="task-edit-content" />
              {editTask.type === "post" && (
                <>
                  <div className="flex gap-2">
                    {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
                      const Icon = cfg.icon;
                      const active = editForm.platforms.includes(k);
                      return (
                        <button key={k} onClick={() => setEditForm(f => ({ ...f, platforms: active ? f.platforms.filter(x => x !== k) : [...f.platforms, k] }))}
                          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium ${active ? "text-white" : "bg-[#0b0811] text-slate-500 border border-white/5"}`}
                          style={active ? { background: cfg.color } : {}}>
                          <Icon className="w-3.5 h-3.5" /> {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                  <Input value={(editForm.hashtags || []).join(", ")} onChange={e => setEditForm(f => ({ ...f, hashtags: e.target.value.split(",").map(h => h.trim().replace("#", "")).filter(Boolean) }))}
                    placeholder="Hashtags (separes par virgule)" className="bg-[#0b0811] border-white/10 text-white text-xs" />
                </>
              )}
              <Input type="datetime-local" value={editForm.scheduled_at} onChange={e => setEditForm(f => ({ ...f, scheduled_at: e.target.value }))}
                className="bg-[#0b0811] border-white/10 text-white" />
              <div className="flex gap-2">
                <Button onClick={saveEdit} disabled={saving} className="flex-1 bg-[#10b981] text-white font-bold" data-testid="save-task-edit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Sauvegarder
                </Button>
                <Button onClick={() => { saveEdit().then(() => validateTask(editTask.task_id)); }}
                  disabled={saving} className="flex-1 bg-blue-500 text-white font-bold" data-testid="save-and-validate">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Sauvegarder et valider
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!showPreview} onOpenChange={(v) => { if (!v) setShowPreview(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Apercu</DialogTitle></DialogHeader>
          {showPreview && (
            <div className="mt-2 space-y-3">
              <div className="flex gap-2 flex-wrap">
                {showPreview.type === "post" && (showPreview.platforms || []).map(pl => {
                  const cfg = PLATFORM_CONFIG[pl];
                  if (!cfg) return null;
                  const Icon = cfg.icon;
                  return <span key={pl} className="flex items-center gap-1 text-xs font-bold" style={{ color: cfg.color }}><Icon className="w-4 h-4" /> {cfg.label}</span>;
                })}
                {showPreview.type === "campaign" && (
                  <span className="flex items-center gap-1 text-xs font-bold text-[#3b82f6]"><Mail className="w-4 h-4" /> Campagne : {showPreview.name}</span>
                )}
              </div>
              <div className="bg-[#0b0811] border border-white/10 rounded-lg p-4">
                {showPreview.subject && <p className="text-xs text-slate-400 mb-2">Objet : {showPreview.subject}</p>}
                <p className="text-sm text-slate-200 whitespace-pre-wrap">{showPreview.content}</p>
                {showPreview.hashtags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3 pt-2 border-t border-white/5">
                    {showPreview.hashtags.map((h, i) => <span key={i} className="text-xs text-[#10b981]">#{h}</span>)}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
